/**
 * Where to send someone after signing in — a same-origin path, or `/`.
 *
 * The same rule `SendMagicLinkDto` enforces server-side, applied here too
 * because the popup sign-in never reaches the server with it: anything
 * absolute, protocol-relative (`//evil.com`) or backslashed is an
 * open-redirect off our own login page.
 */
export function safeRedirect(value: string | null): string {
  if (!value || !/^\/(?!\/|\\)/.test(value) || value.startsWith('/login')) {
    return '/';
  }
  return value;
}

/**
 * `/login/onboarding`, for a signed-in user who has not finished signup.
 *
 * Under `/login` on purpose: the extension can only receive a session from a
 * page matching its `externally_connectable` pattern (`/login*`), and a new
 * user signing in from the extension is handed the session only once
 * onboarding is done. Carries `redirect` and the extension's `source`/`extId`
 * through; drops anything else (a magic link's one-time Firebase params).
 */
export function onboardingPathFor(params: URLSearchParams): string {
  return withSignInParams('/login/onboarding', params);
}

/**
 * `path` with only the sign-in flow's own params from `params` — `redirect`
 * and the extension's `source`/`extId` — so they survive a hop between /login
 * pages.
 */
export function withSignInParams(
  path: string,
  params: URLSearchParams,
): string {
  const kept = new URLSearchParams();
  for (const key of ['redirect', 'source', 'extId']) {
    const value = params.get(key);
    if (value !== null) kept.set(key, value);
  }
  const query = kept.toString();
  return query ? `${path}?${query}` : path;
}

/** `/login`, remembering where the visitor was going. */
export function loginPathFor(target: string): string {
  const redirect = safeRedirect(target);
  return redirect === '/'
    ? '/login'
    : `/login?redirect=${encodeURIComponent(redirect)}`;
}
